/**
 * GridZoomControl - smooth zoom for a media grid (zoom out / zoom in).
 *
 * The value is the tile's minimum width in pixels; `gridZoomStyle` turns it into
 * an `auto-fill` grid that grows and shrinks the tiles continuously (more px =
 * bigger tiles, fewer per row). The buttons can be held down and the zoom keeps
 * moving (requestAnimationFrame). Ported from web_admin, same design, wired to
 * web_crm's i18n. The parent owns and stores the value.
 */
import type { CSSProperties } from "react";
import { useCallback, useRef } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { useT } from "../i18n/I18nProvider";

/** The tile's minimum-width range in pixels (the zoom range). */
export const TILE_MIN = 90;
export const TILE_MAX = 360;
/** A comfortable default zoom (about a mid-sized tile). */
export const TILE_DEFAULT = 160;

const HOLD_SPEED = 200; // px per second while a button is held
const TAP_STEP   = 28;  // the shift a single click is guaranteed to make

export function GridZoomControl({
  value, onChange, min = TILE_MIN, max = TILE_MAX, slider = false,
}: {
  value:    number;
  onChange: (n: number) => void;
  min?:     number;
  max?:     number;
  /** Also show a draggable slider between the buttons (pixel-exact zoom). */
  slider?:  boolean;
}) {
  const t = useT();
  const rafRef   = useRef<number | null>(null);
  const accumRef = useRef(0);          // how far the current hold has moved
  const valueRef = useRef(value);
  valueRef.current = value;

  const apply = useCallback((delta: number) => {
    const next = Math.round(Math.min(max, Math.max(min, valueRef.current + delta)));
    if (next !== valueRef.current) { valueRef.current = next; onChange(next); }
  }, [min, max, onChange]);

  const stop = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  // Holding: keep zooming smoothly until the pointer is released.
  const startHold = useCallback((dir: 1 | -1) => {
    stop();
    accumRef.current = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000; last = now;
      const step = dir * HOLD_SPEED * dt;
      accumRef.current += Math.abs(step);
      apply(step);
      // Stop at the limit - there is nowhere further to go, and rAF should not
      // keep running empty frames.
      if ((dir < 0 && valueRef.current <= min) || (dir > 0 && valueRef.current >= max)) { stop(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [apply, stop, min, max]);

  // Release: stop, and if the press was short (little accumulated), top it up
  // so a single click still makes a noticeable but smooth move.
  const endHold = useCallback((dir: 1 | -1) => {
    stop();
    if (accumRef.current < TAP_STEP) apply(dir * (TAP_STEP - accumRef.current));
    accumRef.current = 0;
  }, [apply, stop]);

  function holdButton(dir: 1 | -1, icon: React.ReactNode, label: string, disabled: boolean) {
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled}
        onPointerDown={(e: React.PointerEvent<HTMLButtonElement>) => {
          if (disabled || e.button !== 0) return;
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          startHold(dir);
        }}
        onPointerUp={(e) => { if (e.button === 0) endHold(dir); }}
        onPointerCancel={stop}
        onLostPointerCapture={stop}
        // Keyboard (Enter/Space fires a click with no pointer, detail === 0): one step.
        onClick={(e) => { if (e.detail === 0 && !disabled) apply(dir * TAP_STEP); }}
        className="size-9 inline-flex items-center justify-center text-muted-foreground transition-colors hover:bg-accent/50 disabled:opacity-40 disabled:hover:bg-transparent select-none touch-none"
      >
        {icon}
      </button>
    );
  }

  return (
    <div
      className="inline-flex items-center border rounded-md overflow-hidden shrink-0 h-9"
      title={t("common.zoom")}
    >
      {holdButton(-1, <ZoomOut className="size-4" />, t("common.zoomOut"), value <= min)}
      {slider && (
        <>
          <span className="self-stretch w-px bg-border" />
          {/* The slider - pixel-exact smooth zoom (step=1). */}
          <input
            type="range"
            min={min}
            max={max}
            step={1}
            value={Math.round(value)}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label={t("common.zoom")}
            className="w-24 mx-2.5 cursor-pointer"
            style={{ accentColor: "var(--color-primary)" }}
          />
        </>
      )}
      <span className="self-stretch w-px bg-border" />
      {holdButton(1, <ZoomIn className="size-4" />, t("common.zoomIn"), value >= max)}
    </div>
  );
}

/**
 * The grid's inline style: `auto-fill` columns, each at least `tile` px wide but
 * never wider than the container (`min(..., 100%)`). `1fr` then stretches them
 * evenly to fill the row. Changing `tile` scales the tiles smoothly.
 */
export function gridZoomStyle(tile: number): CSSProperties {
  return { gridTemplateColumns: `repeat(auto-fill, minmax(min(${tile}px, 100%), 1fr))` };
}

/** Read a stored zoom value from localStorage, clamped to [min, max]. */
export function readStoredZoom(key: string, fallback = TILE_DEFAULT, min = TILE_MIN, max = TILE_MAX): number {
  if (typeof window === "undefined") return fallback;
  const n = Number(window.localStorage.getItem(key));
  return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
}
